import { useState, useEffect, useCallback, useRef } from 'react';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

// Procesa los eventos por hora de HOY ({ dFecha, iHora, vTipoEvento, iTotal, idEstadoTicket })
const processTodayEvents = (events = []) => {
  const hoursMap = {};
  for (let h = 0; h < 24; h++) {
    hoursMap[h] = { iCreados: 0, iPendientes: 0, iResueltos: 0 };
  }

  if (Array.isArray(events)) {
    events.forEach(evt => {
      const tipo = (evt.vTipoEvento || evt.tipoEvento || '').toUpperCase();
      const estado = Number(evt.idEstadoTicket ?? evt.iEstadoTicket ?? 0);
      const count = Number(evt.iTotal ?? evt.total ?? 0);
      const hora = Number(evt.iHora ?? evt.hora ?? 0);

      if (hoursMap[hora]) {
        if (tipo === 'CREACION') {
          hoursMap[hora].iCreados += count;
        } else if (tipo === 'CIERRE') {
          hoursMap[hora].iResueltos += count;
        } else if (tipo === 'PENDIENTE') {
          hoursMap[hora].iPendientes += count;
        } else if (estado > 0) {
          // Cuando el SP retorna directamente idEstadoTicket (1: Abierto/Pendiente, 2: Resuelto, 3: En progreso)
          hoursMap[hora].iCreados += count;
          if (estado === 1) {
            hoursMap[hora].iPendientes += count;
          } else if (estado === 2) {
            hoursMap[hora].iResueltos += count;
          }
        }
      }
    });
  }

  return Object.keys(hoursMap).map(h => ({
    iHora: Number(h),
    iCreados: hoursMap[h].iCreados,
    iPendientes: hoursMap[h].iPendientes,
    iResueltos: hoursMap[h].iResueltos,
    iCantidad: hoursMap[h].iResueltos
  }));
};

const formatLocalDateStr = dt => {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Procesa los eventos semanales ({ dFecha, vDiaSemana, vTipoEvento, iTotal, idEstadoTicket })
const processWeeklyEvents = (events = []) => {
  const dayNamesEs = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const today = new Date();
  const daysMap = {};
  const weeklyTrend = [];

  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(today.getDate() - i);
    const dateStr = formatLocalDateStr(d);
    const dayName = dayNamesEs[d.getDay()];

    daysMap[dateStr] = {
      vFecha: dateStr,
      vNombreDia: dayName,
      iCreados: 0,
      iPendientes: 0,
      iResueltos: 0
    };
    weeklyTrend.push(daysMap[dateStr]);
  }

  if (Array.isArray(events)) {
    events.forEach(evt => {
      const rawDate = String(evt.dFecha || evt.fecha || '');
      let dateKey = '';
      if (/^\d{4}-\d{2}-\d{2}/.test(rawDate)) {
        dateKey = rawDate.slice(0, 10);
      } else {
        const parsed = new Date(rawDate);
        if (!isNaN(parsed.getTime())) {
          dateKey = formatLocalDateStr(parsed);
        }
      }

      const tipo = (evt.vTipoEvento || evt.tipoEvento || '').toUpperCase();
      const estado = Number(evt.idEstadoTicket ?? evt.iEstadoTicket ?? 0);
      const count = Number(evt.iTotal ?? evt.total ?? 0);

      if (daysMap[dateKey]) {
        if (tipo === 'CREACION') {
          daysMap[dateKey].iCreados += count;
        } else if (tipo === 'CIERRE') {
          daysMap[dateKey].iResueltos += count;
        } else if (tipo === 'PENDIENTE') {
          daysMap[dateKey].iPendientes += count;
        } else if (estado > 0) {
          // Clasificación por idEstadoTicket (1: Pendiente/Abierto, 2: Resuelto, 3: En progreso)
          daysMap[dateKey].iCreados += count;
          if (estado === 1) {
            daysMap[dateKey].iPendientes += count;
          } else if (estado === 2) {
            daysMap[dateKey].iResueltos += count;
          }
        }
      }
    });
  }

  return weeklyTrend;
};

export const useDashboardMetrics = (idUsuarioAsignado = null) => {
  const [metrics, setMetrics] = useState({
    iTotalHoy: 0,
    iTotalAyer: 0,
    iPendientes: 0,
    iEnProgreso: 0,
    iResueltosHoy: 0,
    iTotalHistorico: 0,
    iTotalResueltos: 0,
    iTiempoRespuestaPromedio: 0,
    iTiempoCierrePromedio: 0,
    iTiempoRespuestaPromedioHoy: 0,
    iTiempoCierrePromedioHoy: 0,
    hourlyResolutions: [],
    weeklyTrend: []
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isWeekLoading, setIsWeekLoading] = useState(false);
  const [error, setError] = useState(null);
  const { token } = useToken();
  const { showErrorToast } = useToast();
  const isFetchingRef = useRef(false);
  const isFetchingWeekRef = useRef(false);
  const hasLoadedWeekRef = useRef(false);

  // Consulta por defecto: KPIs + Gráfica de HOY únicamente
  const fetchTodayMetrics = useCallback(async () => {
    if (!token || isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const hasAgent = idUsuarioAsignado && idUsuarioAsignado !== 'todos' && idUsuarioAsignado !== 'all';
      const queryParam = hasAgent ? `?idUsuarioAsignado=${idUsuarioAsignado}` : '';
      const metricsUrl = `/api/SupportEngine/GetDashboardMetrics${queryParam}`;
      const chartTodayUrl = `/api/SupportEngine/GetDashboardChartToday${queryParam}`;

      const headers = {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token
      };

      const [metricsResp, chartResp] = await Promise.allSettled([
        fetch(metricsUrl, { headers }),
        fetch(chartTodayUrl, { headers })
      ]);

      let rawMetrics = {};
      if (metricsResp.status === 'fulfilled' && metricsResp.value.ok) {
        const mJson = await metricsResp.value.json();
        rawMetrics = mJson.data || mJson;
      }

      let todayEvents = [];
      if (chartResp.status === 'fulfilled' && chartResp.value.ok) {
        const cJson = await chartResp.value.json();
        todayEvents = cJson.data || cJson || [];
      }

      const hourlyResolutions = processTodayEvents(todayEvents);

      setMetrics(prev => ({
        ...prev,
        iTotalHoy: rawMetrics.iTotalHoy ?? rawMetrics.totalHoy ?? 0,
        iTotalAyer: rawMetrics.iTotalAyer ?? rawMetrics.totalAyer ?? 0,
        iPendientes: rawMetrics.iPendientes ?? rawMetrics.pendientes ?? 0,
        iEnProgreso: rawMetrics.iEnProgreso ?? rawMetrics.enProgreso ?? 0,
        iResueltosHoy: rawMetrics.iResueltosHoy ?? rawMetrics.resueltosHoy ?? 0,
        iTotalHistorico: rawMetrics.iTotalHistorico ?? rawMetrics.totalHistorico ?? 0,
        iTotalResueltos: rawMetrics.iTotalResueltos ?? rawMetrics.totalResueltos ?? 0,
        iTiempoRespuestaPromedio: rawMetrics.iTiempoRespuestaPromedio ?? rawMetrics.iTiempoRespuestaPromedioMinutos ?? 0,
        iTiempoCierrePromedio: rawMetrics.iTiempoCierrePromedio ?? rawMetrics.iTiempoResolucionPromedioMinutos ?? 0,
        iTiempoRespuestaPromedioHoy: rawMetrics.iTiempoRespuestaPromedioHoy ?? 0,
        iTiempoCierrePromedioHoy: rawMetrics.iTiempoCierrePromedioHoy ?? 0,
        hourlyResolutions
      }));
    } catch (err) {
      setError(err.message);
      if (showErrorToast) {
        showErrorToast(err.message || 'Error al cargar métricas de hoy');
      }
    } finally {
      isFetchingRef.current = false;
      setIsLoading(false);
    }
  }, [token, idUsuarioAsignado, showErrorToast]);

  // Consulta bajo demanda: Gráfica de la SEMANA (solo cuando el usuario pulsa 'Semana')
  const fetchWeeklyMetrics = useCallback(async (force = false) => {
    if (!token || isFetchingWeekRef.current) return;
    if (hasLoadedWeekRef.current && !force) return;

    isFetchingWeekRef.current = true;
    setIsWeekLoading(true);

    try {
      const hasAgent = idUsuarioAsignado && idUsuarioAsignado !== 'todos' && idUsuarioAsignado !== 'all';
      const queryParam = hasAgent ? `?idUsuarioAsignado=${idUsuarioAsignado}` : '';
      const chartWeekUrl = `/api/SupportEngine/GetDashboardChartWeek${queryParam}`;

      const headers = {
        Accept: 'application/json',
        Authorization: 'Bearer ' + token
      };

      const resp = await fetch(chartWeekUrl, { headers });
      if (resp.ok) {
        const cJson = await resp.json();
        const weekEvents = cJson.data || cJson || [];
        const weeklyTrend = processWeeklyEvents(weekEvents);

        setMetrics(prev => ({ ...prev, weeklyTrend }));
        hasLoadedWeekRef.current = true;
      }
    } catch (err) {
      console.error('Error al cargar métricas semanales:', err);
      if (showErrorToast) {
        showErrorToast('Error al cargar tendencia semanal');
      }
    } finally {
      isFetchingWeekRef.current = false;
      setIsWeekLoading(false);
    }
  }, [token, idUsuarioAsignado, showErrorToast]);

  const reloadAll = useCallback(() => {
    fetchTodayMetrics();
    if (hasLoadedWeekRef.current) {
      fetchWeeklyMetrics(true);
    }
  }, [fetchTodayMetrics, fetchWeeklyMetrics]);

  useEffect(() => {
    const hadWeek = hasLoadedWeekRef.current;
    hasLoadedWeekRef.current = false;
    fetchTodayMetrics();
    if (hadWeek) {
      fetchWeeklyMetrics(true);
    }
  }, [fetchTodayMetrics, fetchWeeklyMetrics]);

  return {
    metrics,
    isLoading,
    isWeekLoading,
    error,
    reloadMetrics: reloadAll,
    fetchWeeklyMetrics
  };
};
